import { useState, useEffect, useRef } from "react";
import { CHORDS, CHORD_PROGRESSION, PATTERN } from "../constants";
import { createPluckSound, createSteelpanSound, playBuffer } from "../audio";

export const useMusicSequencer = ({
  musicStarted,
  gameOver,
  isMuted,
  showBottomPane,
  tempo,
  isDissonant,
  masterVolume,
  onMusicStateUpdate,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [chordIndex, setChordIndex] = useState(0);

  const lastStepTimeRef = useRef(Date.now());

  // 音楽シーケンサーのメインループ
  useEffect(() => {
    if (!musicStarted || gameOver || !showBottomPane) {
      return;
    }

    const intervalId = setInterval(async () => {
      if (isMuted) {
        return;
      }

      const now = Date.now();
      const stepDuration = (60 * 1000) / tempo / 4; // 4分音符のステップ

      if (now - lastStepTimeRef.current > stepDuration) {
        lastStepTimeRef.current = now;

        // 現在のステップの音を再生
        const [noteIndex, volume] = PATTERN[currentStep];
        if (noteIndex !== -1) {
          const chord = CHORD_PROGRESSION[chordIndex];
          let frequency = CHORDS[chord][noteIndex];

          // 不協和音の場合は周波数を微調整
          if (isDissonant) {
            frequency *= 1.05946; // 半音の1/10
          }

          const buffer = await createPluckSound(frequency);
          playBuffer(buffer, volume * masterVolume);
        }

        // 次のステップへ進む
        const nextStep = (currentStep + 1) % 16;
        setCurrentStep(nextStep);

        // 16ステップ完了したら次のコードへ
        let newChordIndex = chordIndex;
        if (nextStep === 0) {
          newChordIndex = (chordIndex + 1) % CHORD_PROGRESSION.length;
          setChordIndex(newChordIndex);
        }

        // 親コンポーネントに状態を通知
        if (onMusicStateUpdate) {
          onMusicStateUpdate(nextStep, CHORD_PROGRESSION[newChordIndex]);
        }
      }
    }, 10); // 10msごとにチェック

    return () => clearInterval(intervalId);
  }, [
    musicStarted,
    gameOver,
    isMuted,
    showBottomPane,
    tempo,
    isDissonant,
    masterVolume,
    currentStep,
    chordIndex,
    onMusicStateUpdate,
  ]);

  // ボトムクリック時の音再生（スティールパン音）
  const playBottomSound = async () => {
    const chord = CHORD_PROGRESSION[chordIndex];
    let frequency = CHORDS[chord][2] * 2; // ルート音の1オクターブ上

    if (isDissonant) {
      frequency *= 1.05946;
    }

    const buffer = await createSteelpanSound(frequency);
    playBuffer(buffer, 0.8 * masterVolume);
  };

  // シーケンサーのリセット
  const resetSequencer = () => {
    setCurrentStep(0);
    setChordIndex(0);
    lastStepTimeRef.current = Date.now();
  };

  return {
    currentStep,
    chordIndex,
    playBottomSound,
    resetSequencer,
  };
};
